import { useCallback, useEffect, useRef, useState } from 'react';
import { isRequestCanceled } from '@srm/core/http';
import { WinnerSelectionService } from '@services/WinnerSelectionService';
import { ITableDemand } from '@models/winnerSelection/ITableDemand';

export interface IWinnerSelectionTableDemandInfo {
    offersCount: number;
    winnerName: string | null;
    winnerNames: string[];
    hasMainOffer: boolean;
    status: 'todo' | 'in-progress' | 'decided';
}

interface IUseTableDemandData {
    data: ITableDemand | undefined;
    isLoading: boolean;
    /** true, когда показываемые данные относятся к ПРЕДЫДУЩЕй позиции, а для
     *  текущего docId загрузка ещё идёт (keep-previous). UI на это время гасит
     *  и блокирует таблицу, чтобы нельзя было оценить «не ту» позицию. */
    isStale: boolean;
    errorText: string | undefined;
    /** Перезагрузить данные текущей позиции (после save в модалках). */
    reload: () => void;
}

/**
 * Загружает таблицу сравнения по docId+tableKey.
 *
 * onDataLoaded получает derived info + docId позиции, для которой ЭТА info
 * актуальна. docId передаётся вторым аргументом потому что parent (TablePositions)
 * мерджит результат в кэш по ключу позиции, а при быстром переключении
 * между позициями старый response может прийти позже смены currentPosition →
 * иначе инфа A записалась бы в кэш B (race condition).
 *
 * Колбэк стабилизируется через ref — иначе loadData пересоздавался бы при
 * каждом ре-рендере parent'а, что приводило бы к бесконечному циклу fetch'ей.
 *
 * AbortController отменяет inflight-запрос при смене docId/tableKey/unmount.
 * Если запрос успел дойти до .then до момента abort'а — флаг signal.aborted
 * не даст обновить state.
 */
export function useTableDemandData(
    docId: string,
    tableKey: string | undefined,
    onDataLoaded?: (info: IWinnerSelectionTableDemandInfo, loadedDocId: string) => void,
): IUseTableDemandData {
    /* Храним data ВМЕСТЕ с docId, для которого она загружена. Это позволяет
     * показывать предыдущую таблицу во время загрузки новой (keep-previous,
     * без мелькания лоадера), отличая её как stale по несовпадению docId. */
    const [loaded, setLoaded] = useState<{ docId: string; table: ITableDemand }>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [errorText, setErrorText] = useState<string>();

    const onDataLoadedRef = useRef(onDataLoaded);
    useEffect(() => {
        onDataLoadedRef.current = onDataLoaded;
    }, [onDataLoaded]);

    /* Ref на текущий AbortController — нужен чтобы reload() (вызываемый
     * после save в модалках) тоже мог абортиться при последующей смене docId. */
    const controllerRef = useRef<AbortController | null>(null);

    const fetchFor = useCallback(
        (targetDocId: string, signal: AbortSignal) => {
            if (!tableKey) {
                console.error('useTableDemandData. tableKey пустой');
                return;
            }
            setIsLoading(true);
            WinnerSelectionService.fetchTableDemandData(targetDocId, tableKey, signal)
                .then((response) => {
                    if (signal.aborted) return;
                    setLoaded({ docId: targetDocId, table: response.data });
                    const mainOffers = response.data.offers.filter((o) => o.result?.toLowerCase() === 'основной');
                    const hasAnyRated = response.data.offers.some((o) => o.summaryScore != null);
                    const status: 'todo' | 'in-progress' | 'decided' =
                        mainOffers.length > 0 ? 'decided' : hasAnyRated ? 'in-progress' : 'todo';
                    const winnerNames = mainOffers.map((o) => o.name).filter((n): n is string => !!n);
                    onDataLoadedRef.current?.(
                        {
                            offersCount: response.data.offers.length,
                            winnerName: winnerNames[0] ?? null,
                            winnerNames,
                            hasMainOffer: mainOffers.length > 0,
                            status,
                        },
                        targetDocId,
                    );
                })
                .catch((reason) => {
                    if (isRequestCanceled(reason, signal)) return;
                    setErrorText(reason);
                })
                .finally(() => {
                    if (!signal.aborted) setIsLoading(false);
                });
        },
        [tableKey],
    );

    /* reload без аргументов — для onSubmit модалок. Использует свежий controller. */
    const reload = useCallback(() => {
        controllerRef.current?.abort();
        const controller = new AbortController();
        controllerRef.current = controller;
        fetchFor(docId, controller.signal);
    }, [docId, fetchFor]);

    useEffect(() => {
        const controller = new AbortController();
        controllerRef.current = controller;
        fetchFor(docId, controller.signal);
        return () => controller.abort();
    }, [docId, fetchFor]);

    const data = loaded?.table;
    /* data есть, но относится к другой позиции → для текущего docId грузится новая. */
    const isStale = loaded != null && loaded.docId !== docId;

    return { data, isLoading, isStale, errorText, reload };
}
